import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Quota, TokenResponse } from '../../../shared/api/contracts/styleTransferSchemas';
import { requestDeviceToken } from '../api/requestDeviceToken';
import {
  useSessionStore,
  selectIsAuthenticated,
  selectQuota,
  selectToken
} from '../state/sessionStore';

type TransferAudience = 'guest' | 'member';

export interface TransferGateState {
  allowed: boolean;
  reasonKey?: string;
  reason?: 'auth' | 'quota';
  errorKey?: string;
}

export interface TransferSessionController {
  token: TokenResponse | null;
  quota: Quota | null;
  ensureToken: () => Promise<TokenResponse>;
  gate: TransferGateState;
  isAuthenticated: boolean;
  quotaBlocked: boolean;
}

export function useTransferSession(audience: TransferAudience): TransferSessionController {
  const { t } = useTranslation();
  const token = useSessionStore(selectToken);
  const quota = useSessionStore(selectQuota);
  const storeIsAuthenticated = useSessionStore(selectIsAuthenticated);
  const setToken = useSessionStore((state) => state.setToken);

  const quotaBlocked =
    audience === 'member' &&
    (quota ? (quota.remaining ?? 0) <= 0 || quota.status === 'blocked' : true);

  const gate = useMemo<TransferGateState>(() => {
    if (audience === 'guest') {
      return { allowed: true };
    }

    if (!storeIsAuthenticated || !token || token.tokenType !== 'user') {
      return {
        allowed: false,
        reason: 'auth',
        reasonKey: 'memberPage.uploadGate.notAuthenticated',
        errorKey: 'memberPage.errors.notAuthenticated'
      };
    }

    if (quotaBlocked) {
      return {
        allowed: false,
        reason: 'quota',
        reasonKey: 'memberPage.uploadGate.quotaDepleted',
        errorKey: 'memberPage.errors.quota'
      };
    }

    return { allowed: true };
  }, [audience, quotaBlocked, storeIsAuthenticated, token]);

  const ensureToken = useCallback(async (): Promise<TokenResponse> => {
    if (audience === 'guest') {
      if (token) {
        return token;
      }
      const issued = await requestDeviceToken();
      setToken(issued);
      return issued;
    }

    if (!token || token.tokenType !== 'user') {
      throw new Error(t('memberPage.errors.notAuthenticated'));
    }
    if (!gate.allowed && gate.reason === 'quota') {
      throw new Error(t('memberPage.errors.quota'));
    }
    return token;
  }, [audience, gate.allowed, gate.reason, setToken, t, token]);

  return {
    token,
    quota: quota ?? null,
    ensureToken,
    gate,
    isAuthenticated: Boolean(token && token.tokenType === 'user'),
    quotaBlocked
  };
}
